import type { CustomFieldConfig, CustomFieldSchema, CustomFieldType } from '@/types/custom-fields';

/**
 * Result of a single-field validation pass.
 *
 * Mirrors the canonical rules implemented server-side in
 * `apps/operations/src/shared/custom-fields/services/custom-field-validation.service.ts`
 * so the form can block bad input BEFORE the create round-trip. The BE remains
 * the source of truth — this helper exists to keep the user out of the
 * "contact saved but values rejected" half-success state the BE flow produces
 * when sequential calls fail (POST /contacts → POST /custom-field-values).
 */
export type ValidationResult = { ok: true } | { ok: false; error: string };

const ok: ValidationResult = { ok: true };

function err(message: string): ValidationResult {
  return { ok: false, error: message };
}

/**
 * Validates a single custom-field value against its definition config.
 *
 * Rules mirrored from `CustomFieldValidationService.validateFieldValue`:
 *  - TEXT     → `string`, optional `maxLength`
 *  - NUMBER   → finite number, optional `min`/`max`/`precision`
 *  - DROPDOWN → string that must be a key in `config.options`
 *  - MULTI_SELECT → array of string keys, `maxSelections`, all keys in `options`
 *  - DATE     → `YYYY-MM-DD` string the JS Date parser accepts as a real day
 *  - CHECKBOX → boolean (deprecated for new fields per NOT-193 but read-only
 *               compatible)
 *  - CURRENCY → integer (cents), optional `min`/`max`
 *
 * `null` / `undefined` are always treated as "no value" → valid. Required-field
 * enforcement is the form's responsibility (it knows whether the field is
 * `activeFieldKeys` / `isRequired`), not this helper's.
 */
export function validateCustomFieldValue(
  fieldType: CustomFieldType,
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (value === null || value === undefined) return ok;

  switch (fieldType) {
    case 'TEXT':
      return validateText(config, value);
    case 'NUMBER':
      return validateNumber(config, value);
    case 'DROPDOWN':
      return validateDropdown(config, value);
    case 'MULTI_SELECT':
      return validateMultiSelect(config, value);
    case 'DATE':
      return validateDate(value);
    case 'CHECKBOX':
      return validateCheckbox(value);
    case 'CURRENCY':
      return validateCurrency(config, value);
    default:
      return err(`Unknown field type: ${String(fieldType)}`);
  }
}

function validateText(
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (typeof value !== 'string') {
    return err(`Expected text, got ${typeof value}`);
  }
  if (config?.maxLength && value.length > config.maxLength) {
    return err(`Exceeds maximum length of ${config.maxLength}`);
  }
  return ok;
}

function validateNumber(
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return err(`Expected a number, got ${typeof value === 'number' ? 'NaN' : typeof value}`);
  }
  if (config?.min !== undefined && value < config.min) {
    return err(`Value ${value} is below minimum ${config.min}`);
  }
  if (config?.max !== undefined && value > config.max) {
    return err(`Value ${value} exceeds maximum ${config.max}`);
  }
  if (config?.precision !== undefined) {
    const parts = String(value).split('.');
    if (parts[1] && parts[1].length > config.precision) {
      return err(
        `Exceeds maximum precision of ${config.precision} decimal place${config.precision === 1 ? '' : 's'}`,
      );
    }
  }
  return ok;
}

function validateDropdown(
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (typeof value !== 'string') {
    return err(`Expected option key string, got ${typeof value}`);
  }
  if (config?.options && config.options.length > 0) {
    const validKeys = new Set(config.options.map((o) => o.key));
    if (!validKeys.has(value)) {
      return err(`Invalid option "${value}"`);
    }
  }
  return ok;
}

function validateMultiSelect(
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (!Array.isArray(value)) {
    return err('Expected array of option keys');
  }
  for (const item of value) {
    if (typeof item !== 'string') {
      return err('Each selection must be an option key string');
    }
  }
  if (config?.options && config.options.length > 0) {
    const validKeys = new Set(config.options.map((o) => o.key));
    for (const key of value) {
      if (!validKeys.has(key as string)) {
        return err(`Invalid option "${key}"`);
      }
    }
  }
  if (config?.maxSelections && value.length > config.maxSelections) {
    return err(`Exceeds maximum selections of ${config.maxSelections}`);
  }
  return ok;
}

function validateDate(value: unknown): ValidationResult {
  if (typeof value !== 'string') {
    return err(`Expected YYYY-MM-DD date, got ${typeof value}`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return err('Date must use YYYY-MM-DD format');
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    return err(`Invalid date "${value}"`);
  }
  return ok;
}

function validateCheckbox(value: unknown): ValidationResult {
  if (typeof value !== 'boolean') {
    return err(`Expected true/false, got ${typeof value}`);
  }
  return ok;
}

function validateCurrency(
  config: CustomFieldConfig | null | undefined,
  value: unknown,
): ValidationResult {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return err('Expected integer cents');
  }
  if (!Number.isInteger(value)) {
    return err('Currency value must be an integer (cents)');
  }
  if (config?.min !== undefined && value < config.min) {
    return err(`Value ${value} is below minimum ${config.min}`);
  }
  if (config?.max !== undefined && value > config.max) {
    return err(`Value ${value} exceeds maximum ${config.max}`);
  }
  return ok;
}

/**
 * Convenience wrapper for callers that already hold a `CustomFieldSchema`.
 * Prepends the field's user-facing label so the returned error reads as a
 * complete inline message (e.g. "Gate Code: value 9999 exceeds maximum 10").
 */
export function validateCustomFieldFromSchema(
  schema: CustomFieldSchema,
  value: unknown,
): ValidationResult {
  const result = validateCustomFieldValue(
    schema.definition.fieldType,
    (schema.definition.config as CustomFieldConfig | null | undefined) ?? null,
    value,
  );
  if (result.ok) return result;
  return err(`${schema.definition.label}: ${result.error}`);
}
